/**
 * Company Detail Component
 * Shows company settings page, reads company from URL param
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, NavLink } from 'react-router-dom';
import { Company, UserRole, BankAccount, User } from '../../types';
import { Card, Button, Input, Select, Badge } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { ROUTES } from '../../routes';
import { uploadsApi } from '../../services/api.service';
import {
  ArrowLeft,
  Building,
  Briefcase,
  Tag,
  Landmark,
  FileText,
  Users,
  Plus,
  Trash2,
  Save,
  Globe,
  MapPin,
  Wallet,
  PieChart,
  X,
  Camera,
  Upload,
  ImageIcon,
  Link2,
  Check,
  AlertCircle,
  Copy,
  Edit3
} from 'lucide-react';

// Tab slug mapping
type TabType = 'details' | 'banking' | 'integration' | 'team';

const TAB_SLUGS: Record<string, TabType> = {
  'detail-umum': 'details',
  'treasury': 'banking',
  'integrasi': 'integration',
  'akses-tim': 'team',
};

const TAB_TO_SLUG: Record<TabType, string> = {
  'details': 'detail-umum',
  'banking': 'treasury',
  'integration': 'integrasi',
  'team': 'akses-tim',
};

// Helper to create URL-safe slug (lowercase, hyphenated)
export const createCompanySlug = (name: string): string => {
  return encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'));
};

// Helper to decode slug back to searchable format
export const decodeCompanySlug = (slug: string): string => {
  return decodeURIComponent(slug).toLowerCase().replace(/-/g, ' ');
};

const BANK_OPTIONS = [
  "Bank Mandiri",
  "Bank BCA",
  "Bank HSBC",
  "Bank BRI",
  "Bank BNI",
  "Bank CIMB Niaga",
  "Bank Danamon",
  "Bank Permata",
  "Bank Mega",
  "Citibank",
  "Jago",
  "Jenius (BTPN)",
  "Maybank",
  "OCBC NISP",
  "Panin Bank",
  "Standard Chartered",
  "UOB Indonesia",
];

const INVESTMENT_OPTIONS = [
  "Schroders",
  "Manulife",
  "Batavia Prosperindo",
  "BNP Paribas",
  "Trimegah",
  "Panin Asset Management",
  "Mandiri Investasi",
  "Danareksa",
  "Bahana TCW",
  "Eastspring Investments",
];

const CURRENCY_OPTIONS = [
  { code: 'IDR', symbol: 'Rp', name: 'Rupiah Indonesia' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
];

interface CompanyDetailProps {
  companies: Company[];
  users: User[];
  userRole: UserRole;
  onUpdateCompany: (company: Company) => void;
  onDeleteCompany: (companyId: string) => void;
}

export const CompanyDetail: React.FC<CompanyDetailProps> = ({
  companies,
  users,
  userRole,
  onUpdateCompany,
  onDeleteCompany
}) => {
  const { companyName, tab } = useParams<{ companyName: string; tab?: string }>();
  const navigate = useNavigate();
  
  // Find company by matching the slug (case-insensitive)
  const company = companies.find(c => {
    const slug = createCompanySlug(c.name);
    const urlSlug = companyName?.toLowerCase();
    return slug === urlSlug || c.name.toLowerCase().replace(/\s+/g, '-') === urlSlug;
  });

  // Determine active tab from URL or default to 'details'
  const activeTab: TabType = tab && TAB_SLUGS[tab] ? TAB_SLUGS[tab] : 'details';

  // Redirect to default tab if no tab specified
  useEffect(() => {
    if (company && !tab) {
      navigate(`${ROUTES.SETTINGS_COMPANIES}/${companyName}/${TAB_TO_SLUG['details']}`, { replace: true });
    }
  }, [company, tab, companyName, navigate]);

  useTitle(company ? `${company.name} - Pengaturan` : 'Perusahaan Tidak Ditemukan');

  const [editFormData, setEditFormData] = useState<Partial<Company>>({});
  const [isSaved, setIsSaved] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [newBankAccount, setNewBankAccount] = useState<Partial<BankAccount>>({ currency: 'IDR', status: 'active', accountType: 'BANK' });
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const SERVICE_ACCOUNT_EMAIL = 'wbb-sheets-sync@stately-avatar-465303-q4.iam.gserviceaccount.com';

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const canEdit = userRole === UserRole.ADMINISTRATOR || userRole === UserRole.STAF_ADMIN;

  useEffect(() => {
    if (company) {
      setEditFormData({ ...company });
    }
  }, [company]);

  // If company not found
  if (!company) {
    return (
      <div className="text-center py-12">
        <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Perusahaan Tidak Ditemukan</h2>
        <p className="text-gray-500 mb-6">Perusahaan yang Anda cari tidak ada atau telah dihapus.</p>
        <Link to={ROUTES.SETTINGS_COMPANIES}>
          <Button variant="secondary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Daftar
          </Button>
        </Link>
      </div>
    );
  }

  const accessibleUsers = users.filter(u =>
    u.role === UserRole.ADMINISTRATOR ||
    u.role === UserRole.STAF_ADMIN ||
    (u.allowedCompanyIds && u.allowedCompanyIds.includes(company.id))
  );

  const handleUpdateDetails = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    const updated = { ...company, ...editFormData };
    onUpdateCompany(updated);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleSaveBankAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBankAccount.bankName || !newBankAccount.accountNumber || !newBankAccount.accountName) return;

    if (editingBankAccount) {
      // Update existing bank account
      const updatedAccount: BankAccount = {
        ...editingBankAccount,
        bankName: newBankAccount.bankName,
        accountNumber: newBankAccount.accountNumber,
        accountName: newBankAccount.accountName,
        swiftCode: newBankAccount.swiftCode,
        currency: newBankAccount.currency || 'IDR',
        accountType: newBankAccount.accountType || editingBankAccount.accountType || 'BANK',
      };

      const updatedCompany: Company = {
        ...company,
        bankAccounts: company.bankAccounts?.map(b => 
          b.id === editingBankAccount.id ? updatedAccount : b
        ) || []
      };

      onUpdateCompany(updatedCompany);
    } else {
      // Create new bank account
      const account: BankAccount = {
        id: `b_${Date.now()}`,
        bankName: newBankAccount.bankName,
        accountNumber: newBankAccount.accountNumber,
        accountName: newBankAccount.accountName,
        swiftCode: newBankAccount.swiftCode,
        currency: newBankAccount.currency || 'IDR',
        status: 'active',
        accountType: newBankAccount.accountType || 'BANK'
      };

      const updatedCompany: Company = {
        ...company,
        bankAccounts: [...(company.bankAccounts || []), account]
      };

      onUpdateCompany(updatedCompany);
    }

    setNewBankAccount({ currency: 'IDR', status: 'active', accountType: 'BANK' });
    setEditingBankAccount(null);
    setIsBankModalOpen(false);
  };

  const handleEditBankAccount = (account: BankAccount) => {
    setEditingBankAccount(account);
    setNewBankAccount({
      bankName: account.bankName,
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      swiftCode: account.swiftCode,
      currency: account.currency || 'IDR',
      status: account.status,
      accountType: account.accountType || 'BANK'
    });
    setIsBankModalOpen(true);
  };

  const handleCloseBankModal = () => {
    setIsBankModalOpen(false);
    setEditingBankAccount(null);
    setNewBankAccount({ currency: 'IDR', status: 'active', accountType: 'BANK' });
  };

  const handleDeleteBankAccount = (accountId: string) => {
    if (!canEdit) return;
    if (!confirm('Hapus rekening ini?')) return;

    const updatedCompany: Company = {
      ...company,
      bankAccounts: company.bankAccounts?.filter(b => b.id !== accountId) || []
    };

    onUpdateCompany(updatedCompany);
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Hanya file gambar yang diperbolehkan');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Ukuran file maksimal 2MB');
      return;
    }

    // Store file for upload
    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    if (!logoFile || !canEdit) return;

    setIsUploadingLogo(true);
    try {
      // Upload file to server
      const uploadedFiles = await uploadsApi.upload('logos', [logoFile]);
      
      if (uploadedFiles && uploadedFiles.length > 0) {
        const uploadedUrl = uploadedFiles[0].url;
        
        const updatedCompany: Company = {
          ...company,
          logoUrl: uploadedUrl
        };

        onUpdateCompany(updatedCompany);
        setIsLogoModalOpen(false);
        setLogoPreview(null);
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Gagal mengupload logo. Silakan coba lagi.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    if (!canEdit) return;
    if (!confirm('Hapus logo perusahaan?')) return;

    const updatedCompany: Company = {
      ...company,
      logoUrl: undefined
    };

    onUpdateCompany(updatedCompany);
  };

  return (
    <div className="space-y-6 animate-fade-in-right relative">
      {/* Header Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to={ROUTES.SETTINGS_COMPANIES}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Kembali ke Perusahaan
        </Link>
      </div>

      {/* Company Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <Building className="w-64 h-64 text-gray-900" />
        </div>

        <div className="relative flex items-start space-x-6">
          {/* Logo/Avatar with Edit Capability */}
          <div className="relative group">
            {company.logoUrl ? (
              <div className="h-24 w-24 rounded-2xl overflow-hidden shadow-lg bg-white border border-gray-200">
                <img
                  src={company.logoUrl}
                  alt={`${company.name} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className={`
                h-24 w-24 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-lg
                ${company.isHolding ? 'bg-linear-to-br from-indigo-600 to-purple-700' : 'bg-linear-to-br from-blue-500 to-blue-600'}
              `}>
                {company.isHolding ? <Briefcase className="w-10 h-10" /> : company.name.charAt(0)}
              </div>
            )}
            
            {/* Edit Overlay */}
            {canEdit && (
              <button
                onClick={() => setIsLogoModalOpen(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="w-6 h-6 text-white" />
              </button>
            )}
          </div>

          <div className="pt-2">
            <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
            <div className="flex items-center space-x-3 mt-2">
              <Badge color={company.status === 'active' ? 'green' : 'gray'}>{company.status}</Badge>
              <span className="text-sm text-gray-500 flex items-center">
                <Tag className="w-3 h-3 mr-1" /> {company.industry}
              </span>
              <span className="text-sm text-gray-500 font-mono">ID: {company.id}</span>
            </div>
            <p className="mt-4 text-sm text-gray-500 max-w-xl">
              {company.isHolding
                ? 'Entitas ini bertindak sebagai perusahaan induk untuk operasi anak perusahaan.'
                : 'Entitas ini adalah unit operasi anak perusahaan.'}
            </p>
          </div>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar / Menu */}
        <div className="lg:col-span-1 space-y-4">
          <nav className="space-y-1">
            <NavLink
              to={`${ROUTES.SETTINGS_COMPANIES}/${companyName}/detail-umum`}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                ${activeTab === 'details' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
              `}
            >
              <FileText className="w-4 h-4 mr-3" />
              Detail Umum
            </NavLink>
            <NavLink
              to={`${ROUTES.SETTINGS_COMPANIES}/${companyName}/treasury`}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                ${activeTab === 'banking' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
              `}
            >
              <Landmark className="w-4 h-4 mr-3" />
              Treasury & Perbankan
            </NavLink>
            <NavLink
              to={`${ROUTES.SETTINGS_COMPANIES}/${companyName}/integrasi`}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                ${activeTab === 'integration' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
              `}
            >
              <Link2 className="w-4 h-4 mr-3" />
              Integrasi Google Sheets
            </NavLink>
            <NavLink
              to={`${ROUTES.SETTINGS_COMPANIES}/${companyName}/akses-tim`}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                ${activeTab === 'team' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
              `}
            >
              <Users className="w-4 h-4 mr-3" />
              Akses Tim
            </NavLink>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">

          {/* --- BANKING TAB --- */}
          {activeTab === 'banking' && (
            <div className="space-y-6 animate-fade-in-up">
              {/* Bank Accounts Section */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Rekening Bank</h3>
                    <p className="text-sm text-gray-500">Kelola rekening bank perusahaan untuk Statement Recorder.</p>
                  </div>
                  {canEdit && (
                    <Button size="sm" onClick={() => {
                      setNewBankAccount({ currency: 'IDR', status: 'active', accountType: 'BANK' });
                      setIsBankModalOpen(true);
                    }}>
                      <Plus className="w-4 h-4 mr-1.5" /> Tambah Bank
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  {company.bankAccounts && company.bankAccounts.filter(a => !a.accountType || a.accountType === 'BANK').length > 0 ? (
                    company.bankAccounts.filter(a => !a.accountType || a.accountType === 'BANK').map(account => (
                      <div key={account.id} className="group border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:border-indigo-300 hover:shadow-sm transition-all bg-white gap-4">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                            <Landmark className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{account.bankName}</h4>
                            <p className="text-sm text-gray-500 font-mono">{account.accountNumber} • {account.currency}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Atas Nama</p>
                            <p className="text-sm font-medium text-gray-700">{account.accountName}</p>
                          </div>
                          {canEdit && (
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleEditBankAccount(account)}
                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBankAccount(account.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                      <Landmark className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <h3 className="text-sm font-medium text-gray-900">Belum ada rekening bank</h3>
                      <p className="text-xs text-gray-500 mt-1">Tambahkan rekening bank untuk Statement Recorder.</p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Investment Accounts Section */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Rekening Investasi</h3>
                    <p className="text-sm text-gray-500">Kelola akun reksadana dan investasi untuk Mutual Fund Recorder.</p>
                  </div>
                  {canEdit && (
                    <Button size="sm" onClick={() => {
                      setNewBankAccount({ currency: 'IDR', status: 'active', accountType: 'INVESTMENT' });
                      setIsBankModalOpen(true);
                    }}>
                      <Plus className="w-4 h-4 mr-1.5" /> Tambah Investasi
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  {company.bankAccounts && company.bankAccounts.filter(a => a.accountType === 'INVESTMENT').length > 0 ? (
                    company.bankAccounts.filter(a => a.accountType === 'INVESTMENT').map(account => (
                      <div key={account.id} className="group border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:border-emerald-300 hover:shadow-sm transition-all bg-white gap-4">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                            <PieChart className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{account.bankName}</h4>
                            <p className="text-sm text-gray-500 font-mono">{account.accountNumber} • {account.currency}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Atas Nama</p>
                            <p className="text-sm font-medium text-gray-700">{account.accountName}</p>
                          </div>
                          {canEdit && (
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleEditBankAccount(account)}
                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBankAccount(account.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                      <PieChart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <h3 className="text-sm font-medium text-gray-900">Belum ada akun investasi</h3>
                      <p className="text-xs text-gray-500 mt-1">Tambahkan akun reksadana untuk Mutual Fund Recorder.</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* --- DETAILS TAB --- */}
          {activeTab === 'details' && (
            <div className="space-y-6 animate-fade-in-up">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Profil Perusahaan</h3>
                    <p className="text-sm text-gray-500">Perbarui informasi badan hukum.</p>
                  </div>
                  {isSaved && (
                    <span className="text-sm text-emerald-600 font-medium animate-pulse">Perubahan disimpan!</span>
                  )}
                </div>
                <form onSubmit={handleUpdateDetails} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Nama Perusahaan"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      disabled={!canEdit}
                    />
                    <Input
                      label="Industri"
                      value={editFormData.industry || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, industry: e.target.value })}
                      disabled={!canEdit}
                    />
                    <Input
                      label="Nomor Pajak (NPWP/Tax ID)"
                      value={editFormData.taxId || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, taxId: e.target.value })}
                      disabled={!canEdit}
                    />
                    <Input
                      label="Negara"
                      startIcon={Globe}
                      value={editFormData.country || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                      disabled={!canEdit}
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Alamat Terdaftar"
                        startIcon={MapPin}
                        value={editFormData.address || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>

                  {canEdit && (
                    <div className="flex justify-end pt-4 border-t border-gray-100">
                      <Button type="submit">
                        <Save className="w-4 h-4 mr-2" /> Simpan Perubahan
                      </Button>
                    </div>
                  )}
                </form>
              </Card>
            </div>
          )}

          {/* --- INTEGRATION TAB --- */}
          {activeTab === 'integration' && (
            <div className="space-y-6 animate-fade-in-up">
              <Card className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Integrasi Google Sheets</h3>
                  <p className="text-sm text-gray-500">Hubungkan spreadsheet untuk sinkronisasi transaksi otomatis.</p>
                </div>

                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Penting: Bagikan spreadsheet dengan email berikut:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-amber-100 px-3 py-2 rounded text-amber-900 select-all block">
                      {SERVICE_ACCOUNT_EMAIL}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className={`p-2 rounded-lg transition-colors ${
                        emailCopied 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      }`}
                      title={emailCopied ? 'Tersalin!' : 'Salin email'}
                    >
                      {emailCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-sm text-amber-700 mt-3">Buka spreadsheet → Klik "Share" → Tambahkan email di atas sebagai <strong>Editor</strong></p>
                </div>

                {(() => {
                  // Helper function to extract spreadsheet ID from URL
                  const extractSpreadsheetId = (input: string): string => {
                    if (!input) return '';
                    if (input.includes('docs.google.com')) {
                      const match = input.match(/\/d\/([a-zA-Z0-9_-]+)/);
                      return match ? match[1] : input;
                    }
                    return input.trim();
                  };

                  const statementId = extractSpreadsheetId(editFormData.statementSpreadsheetId || '');
                  const mutualFundId = extractSpreadsheetId(editFormData.mutualFundSpreadsheetId || '');

                  // Check if spreadsheet IDs are used by other companies
                  const getSpreadsheetUsage = (spreadsheetId: string, fieldType: 'statement' | 'mutualFund') => {
                    if (!spreadsheetId) return { isUsed: false, usedBy: null };
                    
                    for (const c of companies) {
                      if (c.id === company.id) continue; // Skip current company
                      
                      const cStatementId = extractSpreadsheetId(c.statementSpreadsheetId || '');
                      const cMutualFundId = extractSpreadsheetId(c.mutualFundSpreadsheetId || '');
                      
                      if (cStatementId === spreadsheetId || cMutualFundId === spreadsheetId) {
                        return { isUsed: true, usedBy: c.name };
                      }
                    }
                    return { isUsed: false, usedBy: null };
                  };

                  // Check if both fields have the same ID
                  const isSameAsOtherField = statementId && mutualFundId && statementId === mutualFundId;

                  const statementUsage = getSpreadsheetUsage(statementId, 'statement');
                  const mutualFundUsage = getSpreadsheetUsage(mutualFundId, 'mutualFund');

                  const statementValid = statementId && !statementUsage.isUsed && !isSameAsOtherField;
                  const statementInvalid = statementId && (statementUsage.isUsed || isSameAsOtherField);
                  
                  const mutualFundValid = mutualFundId && !mutualFundUsage.isUsed && !isSameAsOtherField;
                  const mutualFundInvalid = mutualFundId && (mutualFundUsage.isUsed || isSameAsOtherField);

                  const hasErrors = statementInvalid || mutualFundInvalid;

                  return (
                    <form onSubmit={(e) => { if (hasErrors) { e.preventDefault(); return; } handleUpdateDetails(e); }} className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <div className="relative">
                            <Input
                              label="Spreadsheet ID - Mutasi Bank"
                              value={editFormData.statementSpreadsheetId || ''}
                              onChange={(e) => setEditFormData({ ...editFormData, statementSpreadsheetId: e.target.value })}
                              disabled={!canEdit}
                              placeholder="Contoh: 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                              className={statementInvalid ? 'border-red-300 focus:border-red-500 focus:ring-red-500 pr-10' : statementValid ? 'border-green-300 focus:border-green-500 focus:ring-green-500 pr-10' : ''}
                            />
                            {statementValid && (
                              <div className="absolute right-3 top-9 text-green-500">
                                <Check className="w-5 h-5" />
                              </div>
                            )}
                            {statementInvalid && (
                              <div className="absolute right-3 top-9 text-red-500">
                                <X className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          {statementUsage.isUsed && (
                            <p className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Spreadsheet ini sudah digunakan oleh <strong className="ml-1">{statementUsage.usedBy}</strong>
                            </p>
                          )}
                          {isSameAsOtherField && statementId && (
                            <p className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Tidak boleh sama dengan Spreadsheet Reksadana
                            </p>
                          )}
                          {!statementInvalid && (
                            <p className="text-xs text-gray-500 mt-1">
                              ID atau URL Google Spreadsheet untuk transaksi dari menu <strong>Mutasi Bank</strong>
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <div className="relative">
                            <Input
                              label="Spreadsheet ID - Reksadana"
                              value={editFormData.mutualFundSpreadsheetId || ''}
                              onChange={(e) => setEditFormData({ ...editFormData, mutualFundSpreadsheetId: e.target.value })}
                              disabled={!canEdit}
                              placeholder="Contoh: 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                              className={mutualFundInvalid ? 'border-red-300 focus:border-red-500 focus:ring-red-500 pr-10' : mutualFundValid ? 'border-green-300 focus:border-green-500 focus:ring-green-500 pr-10' : ''}
                            />
                            {mutualFundValid && (
                              <div className="absolute right-3 top-9 text-green-500">
                                <Check className="w-5 h-5" />
                              </div>
                            )}
                            {mutualFundInvalid && (
                              <div className="absolute right-3 top-9 text-red-500">
                                <X className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          {mutualFundUsage.isUsed && (
                            <p className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Spreadsheet ini sudah digunakan oleh <strong className="ml-1">{mutualFundUsage.usedBy}</strong>
                            </p>
                          )}
                          {isSameAsOtherField && mutualFundId && (
                            <p className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Tidak boleh sama dengan Spreadsheet Mutasi Bank
                            </p>
                          )}
                          {!mutualFundInvalid && (
                            <p className="text-xs text-gray-500 mt-1">
                              ID atau URL Google Spreadsheet untuk transaksi dari menu <strong>Reksadana</strong>
                            </p>
                          )}
                        </div>
                      </div>

                      {canEdit && (
                        <div className="flex justify-end pt-4 border-t border-gray-100">
                          <Button type="submit" disabled={hasErrors ? true : undefined}>
                            <Save className="w-4 h-4 mr-2" /> Simpan Perubahan
                          </Button>
                        </div>
                      )}
                    </form>
                  );
                })()}
              </Card>
            </div>
          )}

          {/* --- TEAM TAB --- */}
          {activeTab === 'team' && (
            <div className="space-y-6 animate-fade-in-up">
              <Card className="p-0 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-white">
                  <h3 className="text-lg font-bold text-gray-900">Akses Pengguna</h3>
                  <p className="text-sm text-gray-500">Pengguna yang memiliki izin ke entitas ini.</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {accessibleUsers.length > 0 ? (
                    accessibleUsers.map(user => (
                      <div key={user.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold mr-4">
                            {user.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <Badge color={user.role === UserRole.ADMINISTRATOR ? 'indigo' : 'blue'}>
                          {user.role}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">Tidak ada pengguna yang ditugaskan.</div>
                  )}
                </div>
                <div className="p-4 bg-gray-50 text-xs text-center text-gray-400">
                  Manajemen akses dilakukan di tab "Pengguna & Akses".
                </div>
              </Card>
            </div>
          )}

        </div>
      </div>

      {/* --- BANK ACCOUNT MODAL --- */}
      {isBankModalOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={handleCloseBankModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${newBankAccount.accountType === 'INVESTMENT' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                    {newBankAccount.accountType === 'INVESTMENT' ? <PieChart className="w-5 h-5" /> : <Landmark className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingBankAccount ? 'Edit Rekening' : (newBankAccount.accountType === 'INVESTMENT' ? 'Tambah Akun Investasi' : 'Tambah Rekening Bank')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {newBankAccount.accountType === 'INVESTMENT' ? 'Untuk Mutual Fund Recorder' : 'Untuk Statement Recorder'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseBankModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form id="bank-form" onSubmit={handleSaveBankAccount} className="space-y-4">
                <Select
                  label={newBankAccount.accountType === 'INVESTMENT' ? 'Platform Investasi' : 'Nama Bank'}
                  value={newBankAccount.bankName || ''}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, bankName: e.target.value })}
                  required
                >
                  <option value="">{newBankAccount.accountType === 'INVESTMENT' ? 'Pilih Platform...' : 'Pilih Bank...'}</option>
                  {(newBankAccount.accountType === 'INVESTMENT' ? INVESTMENT_OPTIONS : BANK_OPTIONS).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </Select>
                <Input
                  label={newBankAccount.accountType === 'INVESTMENT' ? 'Nomor CIF / ID Investor' : 'Nomor Rekening'}
                  value={newBankAccount.accountNumber || ''}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, accountNumber: e.target.value })}
                  required
                  placeholder={newBankAccount.accountType === 'INVESTMENT' ? 'Contoh: INV-12345678' : 'Contoh: 123-456-7890'}
                />
                <Input
                  label="Atas Nama (Pemilik Rekening)"
                  value={newBankAccount.accountName || ''}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, accountName: e.target.value })}
                  required
                  placeholder="Nama Legal Perusahaan"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Mata Uang"
                    value={newBankAccount.currency || 'IDR'}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, currency: e.target.value })}
                  >
                    {CURRENCY_OPTIONS.map(cur => (
                      <option key={cur.code} value={cur.code}>
                        {cur.code} ({cur.symbol}) - {cur.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    label="Kode SWIFT (Opsional)"
                    value={newBankAccount.swiftCode || ''}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, swiftCode: e.target.value })}
                    placeholder="Misal: CENAIDJA"
                  />
                </div>
              </form>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-100">
              <Button variant="secondary" onClick={handleCloseBankModal} className="w-full sm:w-auto">Batal</Button>
              <Button type="submit" form="bank-form" className="w-full sm:w-auto">
                {editingBankAccount ? 'Simpan Perubahan' : 'Simpan Rekening'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* --- LOGO UPLOAD MODAL --- */}
      {isLogoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => {
              setIsLogoModalOpen(false);
              setLogoPreview(null);
            }}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Ubah Logo Perusahaan</h3>
                <button
                  onClick={() => {
                    setIsLogoModalOpen(false);
                    setLogoPreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Current/Preview Logo */}
                <div className="flex flex-col items-center">
                  <div className="h-32 w-32 rounded-2xl overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                      />
                    ) : company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={`${company.name} logo`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-12 h-12 text-gray-300" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {logoPreview ? 'Preview logo baru' : 'Logo saat ini'}
                  </p>
                </div>

                {/* Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">
                    Klik untuk upload gambar
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, atau GIF (maks. 2MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoFileChange}
                    className="hidden"
                  />
                </div>

                {/* Remove Logo Option */}
                {company.logoUrl && !logoPreview && (
                  <button
                    onClick={() => {
                      handleRemoveLogo();
                      setIsLogoModalOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 py-2 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Hapus Logo
                  </button>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-100">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsLogoModalOpen(false);
                  setLogoPreview(null);
                }}
                className="w-full sm:w-auto"
              >
                Batal
              </Button>
              <Button
                onClick={handleSaveLogo}
                disabled={!logoPreview}
                className="w-full sm:w-auto"
              >
                Simpan Logo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
